import { useEffect, useRef, useMemo } from 'react';

interface PageMetaProps {
  title: string;
  description: string;
  path: string;
  ogType?: string;
  jsonLd?: Record<string, unknown> | ((siteUrl: string) => Record<string, unknown>);
}

/**
 * Dynamically updates document head with SEO meta tags and injects JSON-LD structured data.
 * Uses VITE_SITE_URL for canonical and JSON-LD URLs.
 */
export default function PageMeta({ title, description, path, ogType = 'website', jsonLd }: PageMetaProps) {
  const jsonLdScriptRef = useRef<HTMLScriptElement | null>(null);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://litbuyspreadsheets.best';

  const fullUrl = `${siteUrl}${path}`;

  const resolvedJsonLd = useMemo(() => {
    if (!jsonLd) return null;
    if (typeof jsonLd === 'function') return jsonLd(siteUrl);
    return jsonLd;
  }, [jsonLd, siteUrl]);

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:url', fullUrl, true);
    setMeta('og:type', ogType, true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);

    const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonical) {
      canonical.setAttribute('href', fullUrl);
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = fullUrl;
      document.head.appendChild(link);
    }
  }, [title, description, fullUrl, ogType]);

  useEffect(() => {
    if (!resolvedJsonLd) return;

    if (jsonLdScriptRef.current) {
      jsonLdScriptRef.current.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(resolvedJsonLd, null, 2);
    document.head.appendChild(script);
    jsonLdScriptRef.current = script;

    return () => {
      if (jsonLdScriptRef.current) {
        jsonLdScriptRef.current.remove();
      }
    };
  }, [resolvedJsonLd]);

  return null;
}